import { FaRegClock } from 'react-icons/fa6';
import { useState, useCallback } from 'react';
import {
  formatTimeOfDay,
  parseTimeOfDay,
  isValidDuration,
} from '@aldebaran/common/util/date';

interface ResponseDurationFieldProps {
  value?: number;
  label?: string;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  isError?: boolean;
}

export const ResponseDurationField = ({
  value,
  label,
  onChange,
  disabled,
  isError,
}: ResponseDurationFieldProps) => {
  const [error, setError] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState('');

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const previousValue = localValue;
      const inputValue = e.target.value;
      setLocalValue(inputValue);

      if (inputValue) {
        const nextValue = parseTimeOfDay(inputValue);
        if (nextValue !== null) {
          onChange(nextValue);
          setError(false);
          return;
        }
      }

      // Only call onChange when transitioning between empty and non-empty
      const wasEmpty = !previousValue || previousValue.trim() === '';
      const isEmpty = !inputValue || inputValue.trim() === '';

      if (wasEmpty !== isEmpty) {
        if (isEmpty) {
          onChange(null);
        }
      }
    },
    [onChange, localValue],
  );

  const onSubmitResponse = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (e.target?.value) {
        const nextValue = parseTimeOfDay(e.target.value);
        if (nextValue !== null) {
          onChange(nextValue);
          setError(false);
        } else {
          onChange(null);
          setError(true);
        }
      } else {
        onChange(null);
      }
    },
    [onChange],
  );

  const hasContent = isValidDuration(value);

  return (
    <div
      className={`flex items-center gap-3 px-4 h-12 rounded-lg font-medium ${
        isFocused
          ? `border border-indigo-900 bg-white shadow-xs`
          : hasContent
          ? 'border border-gray-200 bg-blue-50 shadow-xs'
          : `border shadow-xs focus-within:border-indigo-900 ${
              isError ? 'bg-red-25 border-red-300' : 'bg-white border-gray-200'
            }`
      }`}
    >
      <FaRegClock className="w-5 h-5" />
      <input
        type="time"
        value={
          isFocused
            ? localValue
            : isValidDuration(value)
            ? formatTimeOfDay(value)
            : ''
        }
        placeholder={label}
        onChange={onInputChange}
        onFocus={() => {
          setIsFocused(true);
          setLocalValue(isValidDuration(value) ? formatTimeOfDay(value) : '');
        }}
        onBlur={(e) => {
          setIsFocused(false);
          onSubmitResponse(e);
        }}
        disabled={disabled}
        step={300}
        className={`
            flex-1 h-full border-none bg-transparent text-base font-medium
            disabled:cursor-not-allowed disabled:opacity-50
            [&::-webkit-calendar-picker-indicator]:hidden
            focus:ring-0 focus:outline-hidden focus:border-none focus:bg-transparent
            ${
              isValidDuration(value) ? '' : 'placeholder:text-lightDefaultColor'
            }
            ${error ? 'text-red-900 placeholder:text-red-700' : ''}
          `}
      />
    </div>
  );
};
